import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
  untracked,
  viewChild,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { growthTone } from '../../../services/growth/growth.store.service';
import { ProfileQualityStoreService } from '../../../services/profile-quality/profile-quality.store.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../../i18n/language.service';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ChartModule } from 'primeng/chart';
import type {
  ActiveElement,
  Chart,
  ChartData,
  ChartEvent,
  ChartOptions,
  ChartType,
  TooltipItem,
  TooltipModel,
} from 'chart.js';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { UIChart } from 'primeng/chart';
import { DateRangePickerComponent, DateRange } from '../../components/date-range-picker/date-range-picker.component';
import {
  BucketCount,
  GenderFilter,
  LevelFilter,
  MonthlyEventCountBySport,
  MonthlyUserCountBySport,
  ReportStoreService,
  SportDefinition,
  SportParticipationReportItem,
  UserStatusFilter,
} from '../../../services/report/report.store.service';

/**
 * A chart, typed by what kind of chart it is.
 *
 * Without the parameter, ChartOptions is the union across every chart type
 * chart.js knows, and nothing assignable to it can be written by hand. That
 * is why data and options were both `any`, and why six callbacks below took
 * `any` as well.
 */
type ChartConfig<T extends ChartType> = { data: ChartData<T>; options: ChartOptions<T> };

/**
 * What chart.js hands an external tooltip handler. The library types the two
 * halves but not the object that carries them, so it is written out here.
 */
type ExternalTooltipContext = { chart: Chart; tooltip: TooltipModel<ChartType> };

const SPORT_COLORS = [
  '#f59e0b',
  '#3b82f6',
  '#10b981',
  '#ec4899',
  '#a855f7',
  '#06b6d4',
  '#ef4444',
  '#84cc16',
  '#8b5cf6',
  '#f97316',
  '#14b8a6',
  '#fbbf24',
  '#60a5fa',
  '#f472b6',
];

const GENDER_COLORS: Record<string, string> = {
  Male: '#3B82F6',
  Female: '#EC4899',
  'Other/unknown': '#A3A3A3',
};

@Component({
  selector: 'bo-report',
  templateUrl: './report.component.html',
  styleUrls: ['./report.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    RouterModule,
    CommonModule,
    ChartModule,
    ButtonModule,
    RippleModule,
    ProgressSpinnerModule,
    DateRangePickerComponent,
  ],
})
export class ReportComponent implements OnInit {
  readonly #profileQuality = inject(ProfileQualityStoreService);

  /**
   * Share of profiles actually carrying the field each chart is built on.
   *
   * A distribution silently computed on the filled minority reads as if it
   * described everyone. Gender is the extreme case: the chart looks complete
   * while resting on 12.5% of the base.
   */
  fieldShare(key: string): number | null {
    const field = this.#profileQuality.data().fields.find((entry) => entry.key === key);
    return field ? field.share : null;
  }

  #reportStore = inject(ReportStoreService);
  #translate = inject(TranslateService);
  #language = inject(LanguageService);

  /** Localized sport label: French name when the UI is in French, else English,
   * falling back to the single name then the id so nothing renders empty. */
  #sportLabel(r: { sportNameFr?: string; sportNameEn?: string; sportName?: string; sportId?: string }): string {
    const fr = this.#language.current() === 'fr';
    return (fr ? r.sportNameFr || r.sportNameEn : r.sportNameEn || r.sportNameFr) || r.sportName || r.sportId || '';
  }

  loading = this.#reportStore.loading;
  allItems = this.#reportStore.allItems;
  filteredItems = this.#reportStore.filteredItems;
  eventsByMonth = this.#reportStore.eventsByMonth;
  eventsByMonthPerSport = this.#reportStore.eventsByMonthPerSport;
  userSummary = this.#reportStore.userSummary;
  usersByMonthPerSport = this.#reportStore.usersByMonthPerSport;
  genderSummary = this.#reportStore.genderSummary;
  ageSummary = this.#reportStore.ageSummary;
  usersWithoutSport = this.#reportStore.usersWithoutSport;

  selectedSports = signal<string[]>([]);
  showSportFilter = signal(false);
  dateRange = signal<DateRange>({ startDate: null, endDate: null });

  /** Which quick preset is active, if the current range came from one. */
  activePreset = signal<'30d' | '90d' | '6m' | '12m' | null>('30d');

  /** People who did not fill the field: kept out of the charts, said aside
   * with the share of that chart's base they represent. */
  unknownGender = signal<{ count: number; share: number }>({ count: 0, share: 0 });
  unknownAge = signal<{ count: number; share: number }>({ count: 0, share: 0 });
  unknownLevel = signal<{ count: number; share: number }>({ count: 0, share: 0 });
  unknownSport = signal<{ count: number; share: number }>({ count: 0, share: 0 });

  /** The unknown count against the global base of the period. */
  #unknownInfo(unknown: number): { count: number; share: number } {
    const total = this.totalUsers();
    return { count: unknown, share: total > 0 ? Math.round((unknown / total) * 1000) / 10 : 0 };
  }
  selectedUserStatus = signal<UserStatusFilter>('all');
  pendingGender = signal<GenderFilter>('all');
  selectedGender = signal<GenderFilter>('all');
  selectedLevel = signal<LevelFilter>('all');
  selectedCity = signal<string>('');

  pendingSports = signal<string[]>([]);
  pendingDateRange = signal<DateRange>({ startDate: null, endDate: null });
  pendingUserStatus = signal<UserStatusFilter>('all');

  selectedSportsCount = computed(() => this.selectedSports().length);

  allSportDefs = computed<SportDefinition[]>(() => this.#reportStore.sportDefinitions());

  dateRangePicker = viewChild(DateRangePickerComponent);

  /** Reference to the trend line p-chart so we can access its Chart.js instance */
  @ViewChild('trendChart') trendChartRef?: UIChart;

  totalsChart = signal<ChartConfig<'doughnut'> | null>(null);
  genderChart = signal<ChartConfig<'doughnut'> | null>(null);
  ageChart = signal<ChartConfig<'bar'> | null>(null);
  levelChart = signal<ChartConfig<'bar'> | null>(null);
  eventsLineChart = signal<ChartConfig<'line'> | null>(null);
  userTypeChart = signal<ChartConfig<'doughnut'> | null>(null);
  globalGenderChart = signal<ChartConfig<'doughnut'> | null>(null);

  /** Index of the dataset currently highlighted from legend hover, or null */
  hoveredLegendIndex = signal<number | null>(null);

  /** Track the last hovered dataset index so mouseleave can reset correctly */
  #lastHoveredDatasetIndex: number | null = null;

  #activeRows = computed(() => {
    const selected = this.selectedSports();
    const rows = this.allItems();
    if (selected.length === 0) return rows;
    return rows.filter((r) => selected.includes(r.sportId));
  });

  totalParticipants = computed(() => this.#activeRows().reduce((s, r) => s + (r.totalParticipants || 0), 0));

  /**
   * Year-over-year verdicts: the same window one year earlier, fetched by the
   * store alongside every load. Green progresses, red degrades, orange is a
   * move too small to mean anything.
   */
  #yoy(current: number, previous: number | undefined) {
    if (previous === undefined || previous === 0) {
      return null;
    }
    const value = Math.round(((current - previous) / previous) * 1000) / 10;
    return { value, tone: growthTone(value) };
  }

  yoyUsers = computed(() => this.#yoy(this.totalUsers(), this.#reportStore.compare()?.users));
  yoyEvents = computed(() => this.#yoy(this.totalEvents(), this.#reportStore.compare()?.events));
  yoyParticipants = computed(() => this.#yoy(this.totalParticipants(), this.#reportStore.compare()?.participants));

  totalUsers = computed(() => this.userSummary().totalUsers);
  activeUsers = computed(() => this.userSummary().activeUsers);
  inactiveUsers = computed(() => this.userSummary().inactiveUsers);

  mostPopularSport = computed(() => {
    const rows = this.#activeRows();
    if (!rows.length) return null;
    const best = rows.reduce((b, r) => ((r.totalParticipants || 0) > (b.totalParticipants || 0) ? r : b));
    return {
      ...best,
      frLabel: this.#sportLabel(best),
      iconUrl: this.#getSportIconUrl(best.sportId),
    };
  });

  averageAge = computed(() => {
    const AGE_MID: Record<string, number> = {
      '<18': 15,
      '18-24': 21,
      '25-34': 29.5,
      '35-44': 39.5,
      '45-54': 49.5,
      '55-64': 59.5,
      '65+': 70,
      '55+': 62,
    };
    let total = 0;
    let count = 0;
    this.ageSummary().forEach((b) => {
      const mid = AGE_MID[b.key];
      if (mid) {
        total += mid * b.count;
        count += b.count;
      }
    });
    return count > 0 ? (total / count).toFixed(1) : '—';
  });

  totalEvents = computed(() => {
    if (this.selectedSports().length > 0) {
      return this.#activeRows().reduce((s, r) => s + (r.totalEvents || 0), 0);
    }
    return this.eventsByMonth().reduce((s, m) => s + m.count, 0);
  });

  legendItems = computed(() => {
    const rows = this.#activeRows();
    const items = rows.map((r, i) => {
      const color = SPORT_COLORS[i % SPORT_COLORS.length];
      const count = r.totalUsers || 0;
      return {
        label: this.#sportLabel(r),
        rawLabel: this.#sportLabel(r),
        count,
        color: count > 0 ? color : '#2a2d3e',
        iconUrl: this.#getSportIconUrl(r.sportId),
        hasData: count > 0,
      };
    });
    const total = items.reduce((sum, item) => sum + item.count, 0) || 1;
    return items.map((item) => {
      return {
        ...item,
        pct: item.hasData ? ((item.count / total) * 100).toFixed(1) : '0.0',
      };
    });
  });

  activeSportsCount = computed(() => this.#activeRows().filter((row) => (row.totalUsers || 0) > 0).length);

  userTypeLegend = computed(() => {
    const s = this.userSummary();
    const total = s.totalUsers || 1;
    return [
      {
        label: this.#translate.instant('report.activeUsers'),
        count: s.activeUsers,
        pct: ((s.activeUsers / total) * 100).toFixed(1),
        color: '#FCA13B',
      },
      {
        label: this.#translate.instant('report.inactiveUsers'),
        count: s.inactiveUsers,
        pct: ((s.inactiveUsers / total) * 100).toFixed(1),
        color: '#64748B',
      },
    ];
  });

  globalGenderLegend = computed(() => {
    const buckets = this.genderSummary();
    const total = buckets.reduce((s, b) => s + b.count, 0) || 1;
    const icons: Record<string, string> = { Male: '♂', Female: '♀', 'Other/unknown': '?' };
    return buckets.map((b) => ({
      label: b.key,
      count: b.count,
      pct: ((b.count / total) * 100).toFixed(1),
      color: GENDER_COLORS[b.key] || '#A3A3A3',
      icon: icons[b.key] || '?',
    }));
  });

  genderDistributionTotal = computed(() => this.genderSummary().reduce((sum, item) => sum + item.count, 0));

  constructor() {
    effect(
      () => {
        const rows = this.#activeRows();
        const monthly = this.eventsByMonth();
        const perSport = this.eventsByMonthPerSport();
        const selected = this.selectedSports();
        const usersByMonth = this.usersByMonthPerSport();
        // The people who did not fill the field would drown the real signal:
        // 85% gray in one slice. They leave the charts and stand aside as an
        // information metric; every share below is computed on the filled base.
        const UNKNOWN_KEYS = ['unknown', 'Unknown', 'Other/unknown'];
        const rawGender = this.genderSummary();
        const rawAge = this.ageSummary();
        const genderSum = rawGender.filter((b) => !UNKNOWN_KEYS.includes(b.key));
        const ageSum = rawAge.filter((b) => !UNKNOWN_KEYS.includes(b.key));
        const sum = (buckets: { count: number }[]) => buckets.reduce((s, b) => s + b.count, 0);
        this.unknownGender.set(this.#unknownInfo(sum(rawGender.filter((b) => UNKNOWN_KEYS.includes(b.key)))));
        this.unknownAge.set(this.#unknownInfo(sum(rawAge.filter((b) => UNKNOWN_KEYS.includes(b.key)))));
        const userSum = this.userSummary();
        const usersWithoutSport = this.usersWithoutSport();

        this.#buildCharts(
          rows,
          monthly,
          perSport,
          selected,
          usersByMonth,
          genderSum,
          ageSum,
          userSum,
          usersWithoutSport,
        );
      },
      { allowSignalWrites: true },
    );

    /*
     * NARROWING THE TABLE IS A SIGNAL WRITE, and has to be declared as one.
     *
     * `filterBySports` sets `filteredItems` from `allItems`, which Angular
     * forbids inside an effect unless the effect says so: it threw NG0600 in
     * production on 5 October 2026 (JAVASCRIPT-ANGULAR-1), three people, four
     * times in eight hours -- the sport filter on this page simply fell over.
     * The effect above it, which writes signals too, has always said so.
     *
     * The shape to aim for is a `computed` rather than an effect, since
     * `filteredItems` is derived and not decided. That waits for the load path
     * to stop setting it in two other places.
     */
    effect(
      () => {
        this.#reportStore.filterBySports(this.selectedSports());
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    // Chart labels are baked at build time, so a language switch reloads the
    // data to rebake them in the new language.
    this.#translate.onLangChange.subscribe(() => this.#loadData(this.dateRange(), this.selectedUserStatus()));
    this.#profileQuality.load();
    const today = new Date();
    const start = new Date(today);
    start.setDate(start.getDate() - 30);
    const initial: DateRange = { startDate: start, endDate: today };

    this.dateRange.set(initial);
    this.pendingDateRange.set(initial);
    this.pendingUserStatus.set('all');
    this.selectedUserStatus.set('all');
    this.showSportFilter.set(true);

    this.#loadData(initial, 'all');
  }

  toggleSportFilterPanel(): void {
    const opening = !this.showSportFilter();
    this.showSportFilter.set(opening);
    if (opening) {
      this.pendingSports.set([...this.selectedSports()]);
      this.pendingDateRange.set({ ...this.dateRange() });
      this.pendingUserStatus.set(this.selectedUserStatus());
    }
  }

  onPendingDateRangeChange(range: DateRange): void {
    this.pendingDateRange.set(range);
  }

  toggleSport(sportId: string): void {
    const current = this.pendingSports();
    const next = current.includes(sportId) ? current.filter((s) => s !== sportId) : [...current, sportId];
    this.pendingSports.set(next);
  }

  clearSportSelection(): void {
    this.pendingSports.set([]);
  }

  applyFilters(): void {
    const newRange = this.pendingDateRange();
    const newStatus = this.pendingUserStatus();

    this.selectedSports.set([...this.pendingSports()]);
    this.dateRange.set({ ...newRange });
    this.selectedUserStatus.set(newStatus);

    this.#loadData(newRange, newStatus);
  }

  onReset(): void {
    const today = new Date();
    const start = new Date(today);
    start.setDate(start.getDate() - 30);
    const initial: DateRange = { startDate: start, endDate: today };

    this.selectedSports.set([]);
    this.pendingSports.set([]);
    this.selectedUserStatus.set('all');
    this.pendingUserStatus.set('all');
    this.dateRange.set(initial);
    this.pendingDateRange.set(initial);

    this.#loadData(initial, 'all');
  }

  isDefSelected(sportId: string): boolean {
    return this.pendingSports().includes(sportId);
  }

  onDateRangeChange(range: DateRange): void {
    this.dateRange.set(range);
  }

  onChartDataSelect(_event: unknown): void {
    // reserved for future drill-down
  }

  // ─── Legend hover: sync legend highlight from legend buttons ────────────────

  onLegendHover(index: number): void {
    this.hoveredLegendIndex.set(index);
    this.#applyDatasetHighlight(index);
  }

  onLegendLeave(): void {
    this.hoveredLegendIndex.set(null);
    this.#resetDatasetHighlight();
  }

  // ─── Apply / reset highlight on Chart.js datasets ──────────────────────────

  #applyDatasetHighlight(activeIndex: number): void {
    // PrimeNG declares UIChart.chart as any. This is the trend chart, which is
    // a line chart, so naming it here is what lets the two loops below see the
    // line-only properties they set.
    const chart = this.trendChartRef?.chart as Chart<'line'> | undefined;
    if (!chart) return;

    this.#lastHoveredDatasetIndex = activeIndex;

    chart.data.datasets.forEach((dataset, i: number) => {
      const color = SPORT_COLORS[i % SPORT_COLORS.length];
      if (i === activeIndex) {
        dataset.borderColor = color;
        dataset.pointBackgroundColor = color;
        dataset.borderWidth = 3;
        dataset.pointRadius = 5;
        dataset.pointHoverRadius = 8;
      } else {
        dataset.borderColor = color + '28';
        dataset.pointBackgroundColor = color + '28';
        dataset.borderWidth = 1;
        dataset.pointRadius = 1;
        dataset.pointHoverRadius = 2;
      }
    });

    chart.update('none');
  }

  #resetDatasetHighlight(): void {
    // PrimeNG declares UIChart.chart as any. This is the trend chart, which is
    // a line chart, so naming it here is what lets the two loops below see the
    // line-only properties they set.
    const chart = this.trendChartRef?.chart as Chart<'line'> | undefined;
    if (!chart) return;

    this.#lastHoveredDatasetIndex = null;

    chart.data.datasets.forEach((dataset, i: number) => {
      const color = SPORT_COLORS[i % SPORT_COLORS.length];
      dataset.borderColor = color;
      dataset.backgroundColor = color + '15';
      dataset.pointBackgroundColor = color;
      dataset.borderWidth = 2;
      dataset.pointRadius = 3;
      dataset.pointHoverRadius = 6;
    });

    chart.update('none');
  }

  // ───────────────────────────────────────────────────────────────────────────

  #getSportIconUrl(sportId: string): string {
    const def = this.#reportStore.sportDefinitions().find((s) => s.sportId === sportId);
    const key = def?.slug ?? def?.icon ?? sportId;
    return `assets/icons/sport/${key}.svg`;
  }

  /** The ranges people actually ask for, one click away from the picker. */
  setGender(gender: GenderFilter): void {
    this.pendingGender.set(gender);
    this.selectedGender.set(gender);
    this.#loadData(this.dateRange(), this.selectedUserStatus());
  }

  setLevel(level: LevelFilter): void {
    this.selectedLevel.set(level);
    this.#loadData(this.dateRange(), this.selectedUserStatus());
  }

  setCity(city: string): void {
    this.selectedCity.set(city);
    this.#loadData(this.dateRange(), this.selectedUserStatus());
  }

  setPreset(preset: '30d' | '90d' | '6m' | '12m'): void {
    const today = new Date();
    const start = new Date(today);
    if (preset === '30d') {
      start.setDate(start.getDate() - 30);
    } else if (preset === '90d') {
      start.setDate(start.getDate() - 90);
    } else if (preset === '6m') {
      start.setMonth(start.getMonth() - 6);
    } else {
      start.setFullYear(start.getFullYear() - 1);
    }
    const range: DateRange = { startDate: start, endDate: today };
    this.activePreset.set(preset);
    this.dateRange.set(range);
    this.pendingDateRange.set(range);
    this.#loadData(range, this.selectedUserStatus());
  }

  #loadData(range: DateRange, userStatus: UserStatusFilter = 'all'): void {
    this.#reportStore.loadAllSportParticipationReport(
      range.startDate ? this.#toISODate(new Date(range.startDate)) : null,
      range.endDate ? this.#toISODate(new Date(range.endDate)) : null,
      this.selectedSports(),
      userStatus,
      this.selectedGender(),
      this.selectedCity(),
      this.selectedLevel(),
    );
  }

  #toISODate(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  #ensureExternalTooltipEl(id: string): HTMLDivElement {
    let el = document.getElementById(id) as HTMLDivElement | null;
    if (el) return el;
    el = document.createElement('div');
    el.id = id;
    el.style.position = 'absolute';
    el.style.pointerEvents = 'none';
    el.style.zIndex = '9999';
    el.style.opacity = '0';
    el.style.transition = 'opacity 80ms ease';
    el.style.background = 'rgba(17, 24, 39, 0.98)';
    el.style.border = '1px solid rgba(252, 161, 59, 0.35)';
    el.style.borderRadius = '10px';
    el.style.boxShadow = '0 10px 30px rgba(0,0,0,0.35)';
    el.style.padding = '8px 10px';
    el.style.color = '#f0f2fa';
    el.style.fontSize = '12px';
    el.style.lineHeight = '1.25';
    el.style.whiteSpace = 'nowrap';
    document.body.appendChild(el);
    return el;
  }

  #externalTooltip = (ctx: ExternalTooltipContext, id: string) => {
    const { chart, tooltip } = ctx;
    const el = this.#ensureExternalTooltipEl(id);
    if (!tooltip || tooltip.opacity === 0) {
      el.style.opacity = '0';
      return;
    }
    const title = (tooltip.title?.length ? tooltip.title[0] : '') as string;
    const bodyLines: string[] = [];
    for (const entry of tooltip.body ?? []) {
      bodyLines.push(...(entry.lines ?? []));
    }
    const headerHtml = title ? `<div style="font-weight:700;margin-bottom:4px">${title}</div>` : '';
    const bodyHtml = bodyLines.length ? bodyLines.map((l) => `<div style="color:#d1d5db">${l}</div>`).join('') : '';
    el.innerHTML = `${headerHtml}${bodyHtml}`;
    const { left, top } = chart.canvas.getBoundingClientRect();
    el.style.left = `${left + window.scrollX + tooltip.caretX + 12}px`;
    el.style.top = `${top + window.scrollY + tooltip.caretY + 12}px`;
    el.style.opacity = '1';
  };

  #buildCharts(
    rows: SportParticipationReportItem[],
    monthly: { month: string; count: number }[],
    perSport: MonthlyEventCountBySport[],
    selected: string[],
    usersByMonth: MonthlyUserCountBySport[],
    genderSum: BucketCount[],
    ageSum: BucketCount[],
    userSum: { totalUsers: number; activeUsers: number; inactiveUsers: number },
    usersWithoutSport: number,
  ): void {
    const ds = getComputedStyle(document.documentElement);
    const textSecondary = ds.getPropertyValue('--text-color-secondary') || '#6b7280';
    const surfaceBorder = ds.getPropertyValue('--surface-border') || '#1e2235';

    const baseDoughnutOptions = {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '68%',
      plugins: { legend: { display: false } },
    };

    const sportDistributionItems = rows.map((r, i) => ({
      label: r.sportName ?? r.sportId,
      count: r.totalUsers || 0,
      color: (r.totalUsers || 0) === 0 ? '#2a2d3e' : SPORT_COLORS[i % SPORT_COLORS.length],
    }));
    const total = sportDistributionItems.reduce((sum, item) => sum + item.count, 0) || 1;
    this.unknownSport.set(this.#unknownInfo(usersWithoutSport));

    this.totalsChart.set({
      data: {
        labels: sportDistributionItems.map((item) => item.label),
        datasets: [
          {
            data: sportDistributionItems.map((item) => item.count),
            backgroundColor: sportDistributionItems.map((item) => item.color),
            borderColor: '#ffffff',
            borderWidth: 2,
            hoverOffset: 8,
          },
        ],
      },
      options: {
        ...baseDoughnutOptions,
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            external: (ctx) => this.#externalTooltip(ctx, 'bo-report-totals-donut-tooltip'),
            callbacks: {
              label: (ctx: TooltipItem<ChartType>) => {
                const val = ctx.parsed;
                if (val === 0) return ` ${ctx.label}: ${this.#translate.instant('report.tooltipNoUsers')}`;
                const pct = ((val / total) * 100).toFixed(1);
                if (ctx.label === 'Unknown') {
                  return ` ${ctx.label}: ${val.toLocaleString()} (${pct}%)`;
                }
                return ` ${ctx.label}: ${val.toLocaleString()} (${pct}%) ${this.#translate
                  .instant('report.participantsWord')
                  .toLowerCase()}`;
              },
            },
          },
        },
      },
    });

    const agg = this.#aggregate(rows);
    const lonelyLevels = agg.level
      .filter((b) => ['unknown', 'Unknown'].includes(b.key))
      .reduce((s, b) => s + b.count, 0);
    agg.level = agg.level.filter((b) => !['unknown', 'Unknown'].includes(b.key));
    this.unknownLevel.set(this.#unknownInfo(lonelyLevels));
    agg.gender = agg.gender.filter((b) => !['unknown', 'Unknown', 'Other/unknown'].includes(b.key));
    agg.age = agg.age.filter((b) => !['unknown', 'Unknown'].includes(b.key));
    const gTotal = agg.gender.reduce((s, b) => s + b.count, 0) || 1;

    this.genderChart.set({
      data: {
        labels: agg.gender.map((b) => b.key),
        datasets: [
          {
            data: agg.gender.map((b) => b.count),
            backgroundColor: agg.gender.map((b) => GENDER_COLORS[b.key] || '#A3A3A3'),
            borderColor: '#ffffff',
            borderWidth: 2,
            hoverOffset: 8,
          },
        ],
      },
      options: {
        ...baseDoughnutOptions,
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            external: (ctx) => this.#externalTooltip(ctx, 'bo-report-gender-donut-tooltip'),
            callbacks: {
              label: (ctx: TooltipItem<ChartType>) => {
                const val = ctx.parsed;
                return ` ${ctx.label}: ${val.toLocaleString()} (${((val / gTotal) * 100).toFixed(1)}%)`;
              },
            },
          },
        },
      },
    });

    const ageBuckets = ['<18', '18-24', '25-34', '35-44', '45-54', '55+', '55-64', '65+'];
    const presentAge = [...new Set(ageSum.map((b) => b.key))].sort(
      (a, b) => ageBuckets.indexOf(a) - ageBuckets.indexOf(b),
    );
    const ageTotals = presentAge.map((k) => ageSum.find((b) => b.key === k)?.count ?? 0);
    const ageLabels = presentAge.map((label) => (label === 'unknown' ? 'Unknown' : label));

    this.ageChart.set({
      data: {
        labels: ageLabels,
        datasets: [
          {
            label: this.#translate.instant('report.usersWord'),
            data: ageTotals,
            backgroundColor: '#F59E0B',
            hoverBackgroundColor: '#FCA13B',
            borderColor: 'transparent',
            borderRadius: 6,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            external: (ctx) => this.#externalTooltip(ctx, 'bo-report-age-bar-tooltip'),
            callbacks: {
              label: (ctx: TooltipItem<ChartType>) => ` ${ctx.label}: ${(ctx.parsed.y ?? 0).toLocaleString()} users`,
            },
          },
        },
        scales: {
          x: { ticks: { color: textSecondary, font: { size: 12 } }, grid: { color: surfaceBorder } },
          y: { ticks: { color: textSecondary, font: { size: 12 } }, grid: { color: surfaceBorder } },
        },
      },
    });

    this.levelChart.set({
      data: {
        labels: agg.level.map((b) => b.key),
        datasets: [
          {
            label: this.#translate.instant('report.participantsWord'),
            data: agg.level.map((b) => b.count),
            backgroundColor: agg.level.map((_, i) => SPORT_COLORS[i % SPORT_COLORS.length]),
            borderColor: 'transparent',
            borderRadius: 6,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y' as const,
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            external: (ctx) => this.#externalTooltip(ctx, 'bo-report-level-bar-tooltip'),
            callbacks: {
              label: (ctx: TooltipItem<ChartType>) =>
                ` ${ctx.label}: ${(ctx.parsed.x ?? 0).toLocaleString()} ${this.#translate
                  .instant('report.participantsWord')
                  .toLowerCase()}`,
            },
          },
        },
        scales: {
          x: { ticks: { color: textSecondary, font: { size: 12 } }, grid: { color: surfaceBorder } },
          y: { ticks: { color: textSecondary, font: { size: 12 } }, grid: { color: '#1a1d2e' } },
        },
      },
    });

    this.userTypeChart.set({
      data: {
        labels: [this.#translate.instant('report.activeUsers'), this.#translate.instant('report.inactiveUsers')],
        datasets: [
          {
            data: [userSum.activeUsers, userSum.inactiveUsers],
            backgroundColor: ['#FCA13B', '#64748B'],
            borderColor: '#ffffff',
            borderWidth: 2,
            hoverOffset: 8,
          },
        ],
      },
      options: {
        ...baseDoughnutOptions,
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            external: (ctx) => this.#externalTooltip(ctx, 'bo-report-user-type-donut-tooltip'),
            callbacks: {
              label: (ctx: TooltipItem<ChartType>) => {
                const val = ctx.parsed;
                const t = userSum.totalUsers || 1;
                return ` ${ctx.label}: ${val.toLocaleString()} (${((val / t) * 100).toFixed(1)}%)`;
              },
            },
          },
        },
      },
    });

    const gSumTotal = genderSum.reduce((s, b) => s + b.count, 0) || 1;

    this.globalGenderChart.set({
      data: {
        labels: genderSum.map((b) => b.key),
        datasets: [
          {
            data: genderSum.map((b) => b.count),
            backgroundColor: genderSum.map((b) => GENDER_COLORS[b.key] || '#A3A3A3'),
            borderColor: '#ffffff',
            borderWidth: 2,
            hoverOffset: 8,
          },
        ],
      },
      options: {
        ...baseDoughnutOptions,
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            external: (ctx) => this.#externalTooltip(ctx, 'bo-report-global-gender-donut-tooltip'),
            callbacks: {
              label: (ctx: TooltipItem<ChartType>) => {
                const val = ctx.parsed;
                return ` ${ctx.label}: ${val.toLocaleString()} (${((val / gSumTotal) * 100).toFixed(1)}%)`;
              },
            },
          },
        },
      },
    });

    // ─── Sport Participation Trends (line) ────────────────────────────────────
    const userPerSportMap = new Map<string, MonthlyUserCountBySport>(usersByMonth.map((s) => [s.sportId, s]));

    const activeItems =
      selected.length > 0 ? this.allItems().filter((r) => selected.includes(r.sportId)) : this.allItems();

    const allUserMonthsSet = new Set<string>();
    activeItems.forEach((item) => {
      userPerSportMap.get(item.sportId)?.months.forEach((m) => allUserMonthsSet.add(m.month));
    });

    const allUserMonths = [...allUserMonthsSet].sort();

    if (allUserMonths.length === 0) {
      this.eventsLineChart.set(null);
      return;
    }

    const userMonthLabels = allUserMonths.map((m) => {
      const [year, month] = m.split('-');
      return new Date(+year, +month - 1).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
    });

    const userLineDatasets = activeItems.map((item, i) => {
      const sportData = userPerSportMap.get(item.sportId);
      const monthMap = new Map(sportData?.months.map((m) => [m.month, m.count]) ?? []);
      const color = SPORT_COLORS[i % SPORT_COLORS.length];
      const hasData = sportData !== undefined;

      return {
        label: this.#sportLabel(item),
        data: allUserMonths.map((m) => monthMap.get(m) ?? 0),
        borderColor: hasData ? color : '#1e2235',
        backgroundColor: hasData ? color + '15' : 'transparent',
        borderWidth: hasData ? 2 : 1,
        borderDash: hasData ? [] : [4, 4],
        pointBackgroundColor: hasData ? color : '#2a2d45',
        pointBorderColor: '#0d0f18',
        pointBorderWidth: 2,
        pointRadius: hasData ? 3 : 1,
        pointHoverRadius: hasData ? 6 : 2,
        tension: 0.4,
        fill: false,
      };
    });

    this.eventsLineChart.set({
      data: { labels: userMonthLabels, datasets: userLineDatasets },
      options: this.#lineOptions(textSecondary, surfaceBorder),
    });
  }

  #lineOptions(textSecondary: string, surfaceBorder: string) {
    return {
      responsive: true,
      maintainAspectRatio: false,
      // Use nearest so hover detects individual dataset points, not the whole column
      interaction: { mode: 'nearest' as const, intersect: false, axis: 'xy' as const },
      plugins: {
        legend: { display: false },
        tooltip: {
          // Show tooltip only for the nearest dataset
          mode: 'nearest' as const,
          intersect: false,
          callbacks: {
            label: (ctx: TooltipItem<ChartType>) =>
              ` ${ctx.dataset.label}: ${(ctx.parsed.y ?? 0).toLocaleString()} participant${
                ctx.parsed.y !== 1 ? 's' : ''
              }`,
          },
        },
      },
      // ── Hover on line: highlight hovered dataset, dim all others ─────────────
      onHover: (event: ChartEvent, elements: ActiveElement[], chart: Chart<'line'>) => {
        if (!elements || elements.length === 0) {
          // Mouse left all elements — reset only if we were hovering something
          if (this.#lastHoveredDatasetIndex !== null) {
            this.#lastHoveredDatasetIndex = null;
            this.hoveredLegendIndex.set(null);
            chart.data.datasets.forEach((dataset, i: number) => {
              const color = SPORT_COLORS[i % SPORT_COLORS.length];
              dataset.borderColor = color;
              dataset.backgroundColor = color + '15';
              dataset.pointBackgroundColor = color;
              dataset.borderWidth = 2;
              dataset.pointRadius = 3;
              dataset.pointHoverRadius = 6;
            });
            chart.update('none');
          }
          return;
        }

        const datasetIndex: number = elements[0].datasetIndex;

        // Skip if already highlighting this dataset
        if (datasetIndex === this.#lastHoveredDatasetIndex) return;

        this.#lastHoveredDatasetIndex = datasetIndex;
        this.hoveredLegendIndex.set(datasetIndex);

        chart.data.datasets.forEach((dataset, i: number) => {
          const color = SPORT_COLORS[i % SPORT_COLORS.length];
          if (i === datasetIndex) {
            dataset.borderColor = color;
            dataset.pointBackgroundColor = color;
            dataset.borderWidth = 3;
            dataset.pointRadius = 5;
            dataset.pointHoverRadius = 8;
          } else {
            dataset.borderColor = color + '28';
            dataset.pointBackgroundColor = color + '28';
            dataset.borderWidth = 1;
            dataset.pointRadius = 1;
            dataset.pointHoverRadius = 2;
          }
        });

        chart.update('none');
      },
      scales: {
        x: { ticks: { color: textSecondary, font: { size: 12 } }, grid: { color: surfaceBorder } },
        y: {
          beginAtZero: true,
          ticks: {
            color: textSecondary,
            font: { size: 11 },
            precision: 0,
            callback: (val: string | number) => Number(val).toLocaleString(),
          },
          grid: { color: surfaceBorder },
        },
      },
    };
  }

  #aggregate(rows: SportParticipationReportItem[]) {
    const genderMap = new Map<string, number>();
    const ageMap = new Map<string, number>();
    const levelMap = new Map<string, number>();

    rows.forEach((row) => {
      row.gender?.forEach((b) => genderMap.set(b.key, (genderMap.get(b.key) || 0) + b.count));
      row.age?.forEach((b) => ageMap.set(b.key, (ageMap.get(b.key) || 0) + b.count));
      row.level?.forEach((b) => levelMap.set(b.key, (levelMap.get(b.key) || 0) + b.count));
    });

    const toSorted = (m: Map<string, number>) =>
      Array.from(m, ([key, count]) => ({ key, count })).sort((a, b) => b.count - a.count);

    return {
      gender: toSorted(genderMap),
      age: toSorted(ageMap),
      level: toSorted(levelMap),
    };
  }
}
